/**
 * VOW Sold Listing 敏感数据清理 (CHIME-67459)
 *
 * 范围：仅 soldDate / soldPrice 两个字段
 * 判定：与 md-detail-info mask 渲染条件保持一致 —— soldProtected.includes(listingStatus)
 *
 * 使用点：
 *   - CSR boot (site/src/index.js)：填 store 前清 GLOBAL_DATA / sitePageJSON
 *
 * SSR 端 (ssr.js genStateScript) 因为是 CommonJS，内联了同等逻辑
 * DOM 层 (house.js) 在 fmtData 形成时按用户验证态控制是否展示
 */

function isListingLike(obj) {
    return (
        !!obj &&
        typeof obj === 'object' &&
        !Array.isArray(obj) &&
        'listingStatus' in obj &&
        'mlsOrg' in obj
    );
}

function isProtectedSoldListing(info) {
    const protectedList = info?.mlsOrg?.soldProtected;
    if (!protectedList || !info?.listingStatus) {
        return false;
    }
    return String(protectedList).includes(info.listingStatus);
}

// CHIME-67459: protected listing 需要清空的敏感字段
//   - sold 日期类: soldDate / soldDateStr / soldDateTime
//   - 租赁日期: leasedDate
//   - 价格类: soldPrice / price / pricePerMonth / pricePerSqft
// 范围扩展原因: md-sold-detail 等模板会读 soldDateStr / price 等字段（非 soldDate/soldPrice），
// 仅清后两个会留下数据泄漏点
//
// 日期字段统一设为空字符串 '' 而非 null：
//   原因: 部分调用方做 new Date(info.soldDate) → new Date(null) = 1970-01-01（valid Date），
//   会被 dateFormat 渲染成 "01/01/1970"。
//   设为 '' → new Date('') = Invalid Date，配合修改后的 dateFormat 会返回 ''。
// 价格字段同样设为空字符串（formatCurrency 对 null / '' 都会 fallback 到 '-'）
const SENSITIVE_KEYS = [
    'soldDate',
    'soldDateStr',
    'soldDateTime',
    'leasedDate',
    'soldPrice',
    'price',
    'pricePerMonth',
    'pricePerSqft'
];

function clearSoldFields(info) {
    SENSITIVE_KEYS.forEach(key => {
        if (key in info) {
            info[key] = '';
        }
    });
}

function walk(node, visited) {
    if (!node || typeof node !== 'object' || visited.has(node)) {
        return;
    }
    visited.add(node);

    if (Array.isArray(node)) {
        node.forEach(item => walk(item, visited));
        return;
    }

    if (isListingLike(node) && isProtectedSoldListing(node)) {
        clearSoldFields(node);
    }

    Object.keys(node).forEach(key => walk(node[key], visited));
}

/**
 * 判断当前 listing 是否 VOW 保护且用户未验证（统一 DOM 层兜底口径）
 * @param {*} info 包含 listingStatus / mlsOrg 的 listing 对象
 * @param {boolean} vowListingsStatus 当前用户的 user.vowListingsStatus
 * @returns {boolean} true 表示需要 DOM 层屏蔽敏感字段
 */
export function isVowProtectedAndUnverified(info, vowListingsStatus) {
    if (!info || vowListingsStatus) {
        return false;
    }
    const soldProtected = info.mlsOrg?.soldProtected || '';
    return !!info.listingStatus && soldProtected.includes(info.listingStatus);
}

/**
 * 原地清理 data 中所有 VOW sold-protected listing 的 soldDate / soldPrice 字段
 * @param {*} data 任意嵌套对象/数组
 * @param {boolean} isUserVerified 当前用户是否已 vow verified（已验证则直接返回）
 * @returns {*} 同一个 data（原地修改）
 */
export function sanitizeVowSoldFields(data, isUserVerified) {
    if (isUserVerified) {
        return data;
    }
    if (!data || typeof data !== 'object') {
        return data;
    }
    walk(data, new WeakSet());
    return data;
}
